import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from 'recharts';
import Layout from '../../components/admin/Layout';
import api from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import './Dashboard.css';

const COLORS = ['#00D4B4', '#0F1B35', '#FF6B35', '#7C3AED', '#059669'];

const StatCard = ({ icon, label, value, color, prefix = '' }) => (
  <div className="stat-card">
    <div className="stat-icon" style={{ background: color + '18', color }}>
      {icon}
    </div>
    <div className="stat-body">
      <div className="stat-value">{prefix}{typeof value === 'number' ? value.toLocaleString() : value}</div>
      <div className="stat-label">{label}</div>
    </div>
  </div>
);

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    api.get('/admin/dashboard')
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load dashboard data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <Layout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
        <div className="spinner" style={{ width: 40, height: 40 }}/>
      </div>
    </Layout>
  );

  const { stats, perEvent, trend, payMethods, recentRegistrations, upcomingEvents } = data || {};
  const trendData = (trend || []).map(t => ({ date: t.date?.slice(5), count: t.count }));
  const pieData   = (payMethods || []).map(m => ({ name: m.method, value: m.count }));

  return (
    <Layout>
    <div className="page-enter">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Overview of your event platform</p>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <StatCard icon="📅" label="Total Events"       value={stats?.totalEvents || 0}     color="#00D4B4" />
        <StatCard icon="👥" label="Total Registrants"  value={stats?.totalRegistrants || 0} color="#0F1B35" />
        <StatCard icon="💰" label="Total Revenue"       value={stats?.totalRevenue?.toFixed(2) || '0.00'} color="#FF6B35" prefix="GHS " />
        <StatCard icon="🔜" label="Upcoming Events"    value={stats?.upcomingEvents || 0}   color="#7C3AED" />
      </div>

      {/* Charts row */}
      <div className="dashboard-grid">
        {/* Registrations per event */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Registrations by Event</h3>
          </div>
          <div className="card-body" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={perEvent || []} layout="vertical" margin={{ left: 0, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false}/>
                <XAxis type="number" tick={{ fontSize: 12 }}/>
                <YAxis type="category" dataKey="title" width={100} tick={{ fontSize: 11 }} tickFormatter={v => v.length > 12 ? v.slice(0,12)+'…' : v}/>
                <Tooltip cursor={{ fill: 'rgba(0,212,180,.06)' }}/>
                <Bar dataKey="count" fill="#00D4B4" radius={[0,4,4,0]} name="Registrations"/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Registration trend */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Registration Trend (30 days)</h3>
          </div>
          <div className="card-body" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                <XAxis dataKey="date" tick={{ fontSize: 11 }}/>
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false}/>
                <Tooltip/>
                <Line type="monotone" dataKey="count" stroke="#00D4B4" strokeWidth={2.5}
                      dot={{ fill: '#00D4B4', r: 3 }} name="Registrations"/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Payment methods */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Payment Methods</h3>
          </div>
          <div className="card-body" style={{ height: 280, display: 'flex', alignItems: 'center' }}>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                    {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
                  </Pie>
                  <Tooltip/>
                  <Legend/>
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', width: '100%' }}>No payment data yet</div>
            )}
          </div>
        </div>

        {/* Upcoming events */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Upcoming Events</h3>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <div className="upcoming-list">
              {(upcomingEvents || []).map((e, i) => (
                <div key={i} className="upcoming-item">
                  <div className="upcoming-info">
                    <div className="upcoming-title">{e.title}</div>
                    <div className="upcoming-date">{e.next_date ? new Date(e.next_date).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : 'TBD'}</div>
                  </div>
                  <div className="upcoming-stats">
                    <span className={`badge ${e.is_free ? 'badge-free' : 'badge-paid'}`}>{e.is_free ? 'Free' : `${e.currency} ${e.price}`}</span>
                    <span className="badge badge-primary">{e.registrants} reg.</span>
                  </div>
                </div>
              ))}
              {!upcomingEvents?.length && <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>No upcoming events</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Recent registrations */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Recent Registrations</h3>
        </div>
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th>Event</th><th>Status</th><th>Date</th>
              </tr>
            </thead>
            <tbody>
              {(recentRegistrations || []).map((r, i) => (
                <tr key={i}>
                  <td><strong>{r.firstname} {r.lastname}</strong></td>
                  <td style={{ color: 'var(--text-secondary)' }}>{r.email}</td>
                  <td>{r.event}</td>
                  <td><span className={`badge badge-${r.status === 'confirmed' ? 'success' : 'warning'}`}>{r.status}</span></td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{new Date(r.registration_date).toLocaleDateString()}</td>
                </tr>
              ))}
              {!recentRegistrations?.length && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '24px' }}>No registrations yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </Layout>
  );
};

export default Dashboard;
